import { useQueryClient } from '@tanstack/react-query';
import usePendingPolicyNotices, {
  POLICY_NOTICES_PENDING_QUERY_KEY,
} from 'hooks/usePendingPolicyNotices';
import useScopedSentryMessage from 'hooks/useScopedSentryMessage';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import PolicyNoticeView from 'storybookConfig/stories/species/PolicyNotice/PolicyNoticeView';
import ApiError from 'utils/ApiError';
import { postPolicyNoticeEvent } from 'utils/api/policies';

/**
 * Shows each pending policy notice in turn, records when it is shown and acknowledged, and moves
 * on once the pending list refreshes.
 */
const PolicyNoticeModal = () => {
  const titleId = useId();
  const queryClient = useQueryClient();
  const logError = useScopedSentryMessage('error');
  const { notices } = usePendingPolicyNotices();
  const shownVersionIds = useRef(new Set<string>());
  const [isPosting, setIsPosting] = useState(false);
  const [hasError, setHasError] = useState(false);

  const current = notices[0];
  const versionId = current?.versionId;

  // The scoped Sentry hook returns a new function every render, so keep it in a ref and out of
  // effect dependencies. Otherwise effects would re-run on every render.
  const logErrorRef = useRef(logError);
  logErrorRef.current = logError;

  // A different notice starts without a stale error.
  useEffect(() => {
    setHasError(false);
  }, [versionId]);

  useEffect(() => {
    if (!versionId || shownVersionIds.current.has(versionId)) return;
    shownVersionIds.current.add(versionId);
    postPolicyNoticeEvent(versionId, 'shown').catch((error) => {
      logErrorRef.current('Failed to record policy notice shown event', {
        extra: { error, versionId },
      });
    });
  }, [versionId]);

  // Rejects when the refetch fails, so a stale list is never mistaken for a refreshed one. The
  // pending-notices query logs the failure itself.
  const refetchPending = useCallback(
    () =>
      queryClient.invalidateQueries(
        { queryKey: [POLICY_NOTICES_PENDING_QUERY_KEY] },
        { throwOnError: true }
      ),
    [queryClient]
  );

  const handleAcknowledge = async () => {
    if (!current || isPosting) return;
    setIsPosting(true);
    setHasError(false);
    try {
      await postPolicyNoticeEvent(current.versionId, 'acknowledged');
    } catch (error) {
      // A 409 means a newer version replaced this one: refetch to show it instead.
      if (!(error instanceof ApiError && error.isConflict)) {
        setHasError(true);
        setIsPosting(false);
        logError('Failed to acknowledge policy notice', {
          extra: { error, versionId: current.versionId },
        });
        return;
      }
    }
    try {
      await refetchPending();
    } catch {
      // The list is stale. A retry posts again (the backend answers a repeated ack with the
      // existing event) and refetches.
      setHasError(true);
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <PolicyNoticeView
      notice={current?.notice}
      onAcknowledge={handleAcknowledge}
      isPosting={isPosting}
      hasError={hasError}
      titleId={titleId}
    />
  );
};

export default PolicyNoticeModal;
